import { useState, useCallback } from 'react';
import {
  validateEmail,
  type EmailValidationResult,
} from '../utils/validation';

export function useEmailValidation() {
  const [error, setError] = useState<string | undefined>();
  const [touched, setTouched] = useState(false);

  const applyResult = useCallback((result: EmailValidationResult) => {
    setError(result.valid ? undefined : result.error);
    return result;
  }, []);

  const validate = useCallback(
    (value: string): EmailValidationResult => {
      setTouched(true);
      return applyResult(validateEmail(value));
    },
    [applyResult]
  );

  const onBlur = useCallback(
    (value: string): EmailValidationResult => {
      setTouched(true);
      return applyResult(validateEmail(value));
    },
    [applyResult]
  );

  const onChange = useCallback(
    (value: string): void => {
      if (touched || error) {
        applyResult(validateEmail(value));
      } else {
        setError(undefined);
      }
    },
    [touched, error, applyResult]
  );

  const setErrorExternal = useCallback((message: string | undefined) => {
    setError(message);
    if (message) {
      setTouched(true);
    }
  }, []);

  const reset = useCallback(() => {
    setError(undefined);
    setTouched(false);
  }, []);

  return {
    error,
    touched,
    validate,
    onBlur,
    onChange,
    setError: setErrorExternal,
    reset,
    isValid: (value: string) => validateEmail(value).valid,
  };
}
